import { useEffect, useState } from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsIcon from "@mui/icons-material/Settings";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from "chart.js";

import {
  Bar,
  Bubble,
  Doughnut,
  Line,
  PolarArea,
  Radar,
  Scatter,
} from "react-chartjs-2";

import { stdObject } from "../../../data/types/stdObject";
import { colors } from "../../../data/colors";
import {
  bubbleData,
  doughnutPolarData,
  radarData,
  scatterData,
} from "../../../data/chartData";
import { sendData } from "../../../services/sendData";
import { useMainContext } from "../../../context/MainContext";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { arrayToObject } from "../../../utils/arrayToObject";
import { getISOWeekNumber } from "../../../utils/getISOWeekNumber";
import { useTranslation } from "react-i18next";
import { getPrefix } from "../../../utils/getPrefix";
import { monthsCodes } from "../SearchBar/data/searchItemUtils";
import { getCurrentDateTime } from "../../../utils/getCurrentDateTime";
import { chartTypes } from "../../../data/types/chartTypes";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
);

export default function Chart({
  code,
  width = 0,
  handleDragStart,
  handleDrop,
}: {
  code: number;
  width: number;
  handleDragStart: (e: number) => void;
  handleDrop: (e: number) => void;
}) {
  const { t } = useTranslation();
  const user: any = useSelector((state: RootState) => state.data.user);
  const userAccess: stdObject = useSelector(
    (state: RootState) => state.data.userAccess,
  );
  const { logoutAction } = useMainContext();
  const [isLoading, setIsLoading] = useState(true);
  const [type, setType] = useState("");
  const [mainConfig, setMainConfig] = useState<stdObject>({
    data: {},
    options: {},
  });

  const standarValues = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
      },
      title: {
        display: true,
        text: "",
      },
    },
  };

  const chartOptions: chartTypes = {
    bar: {
      ...{ ...standarValues },
      scales: {
        y: {
          beginAtZero: true,
        },
      },
    },
    doughnut: standarValues,
    bubble: {
      ...standarValues,
      scales: {
        x: {
          beginAtZero: true,
        },
        y: {
          beginAtZero: true,
        },
      },
    },
    polarArea: {
      ...standarValues,
      scales: {
        r: {
          beginAtZero: true,
        },
      },
    },
  };

  const loadData = async () => {
    setType("");
    const { year, month, day, hours } = getCurrentDateTime(true);
    const payloadChartInfo = {
      code,
      serie: user.CODSUC,
      date: [year, month, day].join("-"),
      week: getISOWeekNumber(),
      hour: hours,
    };
    // console.log("payloadChartInfo", payloadChartInfo);

    const responseChartInfo: stdObject = await sendData({
      endPoint: "callChartInfo",
      payload: payloadChartInfo,
      logoutAction,
    });
    console.log("responseChartInfo", responseChartInfo);
    let {
      success,
      status,
      key,
      dateType,
      info,
      data,
      labels,
      datasets,
      notTime,
      colValue,
    } = responseChartInfo;
    if (success && status) {
      let labelsCodes: string[] = [];
      if (Array.isArray(labels)) {
        labels.reverse();
        labelsCodes = labels;
        labels = labels.map((e: string) => {
          const date_parts = e.split("-");
          switch (dateType) {
            case "MONTH":
              return t(monthsCodes[Number(date_parts[1]) - 1]);
            case "DAY":
              return Number(date_parts[2]);
            case "WEEK":
              return Number(date_parts[1]);
            case "HOUR":
              return Number(date_parts[1]);
            default:
              return e;
          }
        });
      } else {
        labels = data.map((e: stdObject) => e[key]);
      }
      data =
        typeof key === "string" && Array.isArray(data) && data.length > 0
          ? data.map((e: stdObject) => {
              const date_parts = e[key].split(" ")[0].split("-");
              switch (dateType) {
                case "YEAR":
                  e[key] = date_parts[0];
                  break;
                case "MONTH":
                  e[key] = date_parts[0] + "-" + Number(date_parts[1]);
                  break;
                case "DAY":
                  e[key] =
                    date_parts[0] +
                    "-" +
                    Number(date_parts[1]) +
                    "-" +
                    Number(date_parts[2]);
                  break;
                case "WEEK":
                  e[key] =
                    date_parts[0] + "-" + getISOWeekNumber(new Date(e[key]));
                  break;
                case "HOUR":
                  const cHour = e[key].split(" ")[1].split(":")[0];
                  e[key] = Number(date_parts[2]) + "-" + cHour;
                  break;
              }
              return { ...e };
            })
          : [];

      data = arrayToObject({ key, erase: [key], data });

      if (!Array.isArray(datasets)) {
        datasets = [];
      }
      let tempData: stdObject = { data: {}, options: {} };
      switch (info.TIPCHR) {
        case "bar":
        case "line":
        case "curveLine":
          tempData.data = {
            labels,
            datasets: notTime
              ? labels.map((e: string, idx: number) => {
                  const barColor = colors.graphColors[idx];
                  return {
                    label: e,
                    data: data[e]?.[colValue] ? [data[e][colValue]] : [],
                    backgroundColor: barColor,
                    borderColor: barColor,
                    borderWidth: 1,
                  };
                })
              : datasets.map((e: string, idx: number) => {
                  const barColor = colors.graphColors[e[1] === "" ? idx : e[1]];
                  return {
                    label: t(`c.${getPrefix(e[0])}`),
                    data: labelsCodes.map((v: string) => data[v]?.[e[0]] ?? 0),
                    backgroundColor: barColor,
                    borderColor: barColor,
                    borderWidth: 1,
                  };
                }),
          };
          if (info.TIPCHR === "curveLine") {
            tempData.data.datasets = tempData.data.datasets.map(
              (e: stdObject) => {
                return { ...e, tension: 0.4 };
              },
            );
          }
          tempData.options = { ...chartOptions.bar };
          break;
        case "radar":
        case "polarArea":
          tempData.data =
            info.TIPCHR === "radar"
              ? { ...radarData }
              : { ...doughnutPolarData };
          tempData.options = { ...chartOptions.polarArea };
          break;
        case "bubble":
        case "scatter":
          tempData.data =
            info.TIPCHR === "bubble" ? { ...bubbleData } : { ...scatterData };
          tempData.options = { ...chartOptions.bubble };
          break;
        case "doughnut":
          tempData.data = {
            labels,
            datasets: datasets.map((e: string) => {
              return {
                label: t(`c.${getPrefix(e[0])}`),
                data: labelsCodes.map((v: string) => data[v]?.[e[0]] ?? 0),
                backgroundColor: labels.map(
                  (_: any, index: number) => colors.graphColors[index],
                ),
                borderColor: colors.lightGrey3,
                borderWidth: 1,
              };
            }),
          };
          tempData.options = { ...chartOptions.doughnut };
          break;
      }
      tempData.options.plugins!.title!.text = info.NOMCHR;
      setMainConfig({ ...tempData });
      setType(info.TIPCHR);
      return true;
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const renderChart = () => {
    switch (type) {
      case "bubble":
        return <Bubble data={mainConfig.data} options={mainConfig.options} />;
      case "doughnut":
        return <Doughnut data={mainConfig.data} options={mainConfig.options} />;
      case "line":
      case "curveLine":
        return (
          <Line
            data={mainConfig.data}
            options={mainConfig.options as ChartOptions<"line">}
          />
        );
      case "polarArea":
        return (
          <PolarArea data={doughnutPolarData} options={mainConfig.options} />
        );
      case "radar":
        return (
          <Radar
            data={mainConfig.data}
            options={mainConfig.options as ChartOptions<"radar">}
          />
        );
      case "scatter":
        return (
          <Scatter
            data={mainConfig.data}
            options={mainConfig.options as ChartOptions<"scatter">}
          />
        );
      default:
        return <Bar data={mainConfig.data} options={mainConfig.options} />;
    }
  };

  return (
    <div
      draggable={userAccess.V.V1 === 2}
      onDragStart={() => handleDragStart(code)}
      onDragOver={(e) => e.preventDefault()}
      onDrop={() => handleDrop(code)}
      style={{
        position: "relative",
        display: "flex",
        width,
        height: 300,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: colors.lightGrey3,
        padding: 10,
        boxSizing: "border-box",
        borderRadius: 5,
      }}
    >
      {type === "" ? (
        <div className="spinner" style={{ width: 40, height: 40 }} />
      ) : (
        <>
          {renderChart()}
          <RefreshIcon
            onClick={() => loadData()}
            style={{
              position: "absolute",
              top: 5,
              right: 5,
              cursor: "pointer",
              color: colors.analitics,
            }}
          />
          {userAccess.V.V1 === 2 && (
            <SettingsIcon
              style={{
                position: "absolute",
                top: 5,
                left: 5,
                cursor: "pointer",
                color: colors.analitics,
              }}
            />
          )}
        </>
      )}
    </div>
  );
}
